import React from 'react';
import { SAFE_IMAGE_URL } from './safeImageUrl';

// 안전한 최소 마크다운 → React 엘리먼트 렌더러. dangerouslySetInnerHTML 미사용 —
// 모든 사용자 텍스트는 React 텍스트 노드라 자동 이스케이프되어 HTML/스크립트 주입이 불가.
// 지원: 제목(#/##/###) · 굵게(**) · 기울임(*) · 링크([텍스트](url), http(s)/mailto 만) ·
// 이미지(![설명](url), 서버 notice-image 경로 또는 https 만) · 불릿 목록(-/*) · 빈 줄 = 문단 구분.
// 그 외 문법은 평문으로 남긴다.

const SAFE_URL = /^(https?:\/\/|mailto:)/i;
// 이미지 출처 화이트리스트는 safeImageUrl.ts(.ts — jest 로 검증 가능)에 둔다.

// 인라인 파싱: ![설명](url), **굵게**, *기울임*, [텍스트](url) → React 노드 배열.
function parseInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const re = /!\[([^\]]*)\]\(([^)\s]+)\)|\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2] !== undefined) {
      const alt = m[1];
      const url = m[2];
      if (SAFE_IMAGE_URL.test(url)) {
        nodes.push(
          <img key={`${keyPrefix}-${i}`} src={url} alt={alt} loading="lazy" className="max-w-full h-auto rounded my-2" />
        );
      } else {
        nodes.push(alt); // 허용되지 않은 출처 → 설명만 평문으로
      }
    } else if (m[3] !== undefined) {
      const label = m[3];
      const url = m[4];
      if (SAFE_URL.test(url)) {
        nodes.push(
          <a key={`${keyPrefix}-${i}`} href={url} target="_blank" rel="noreferrer noopener" className="text-blue-600 underline">{label}</a>
        );
      } else {
        nodes.push(label); // 허용되지 않은 URL(javascript: 등) → 라벨만 평문으로
      }
    } else if (m[5] !== undefined) {
      nodes.push(<strong key={`${keyPrefix}-${i}`}>{m[5]}</strong>);
    } else if (m[6] !== undefined) {
      nodes.push(<em key={`${keyPrefix}-${i}`}>{m[6]}</em>);
    }
    last = m.index + m[0].length;
    i++;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function renderMarkdown(src: unknown): React.ReactNode {
  if (typeof src !== 'string' || !src.trim()) return null;
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let para: string[] = [];
  let key = 0;
  let i = 0;

  const flushPara = () => {
    if (para.length) {
      const text = para.join('\n');
      blocks.push(<p key={`b${key++}`} className="mb-2 whitespace-pre-wrap">{parseInline(text, `p${key}`)}</p>);
      para = [];
    }
  };

  while (i < lines.length) {
    const line = lines[i];
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      flushPara();
      const level = h[1].length;
      const content = parseInline(h[2], `h${key}`);
      const cls = level === 1 ? 'text-xl font-bold mb-2' : level === 2 ? 'text-lg font-semibold mb-2' : 'text-base font-semibold mb-1';
      if (level === 1) blocks.push(<h1 key={`b${key++}`} className={cls}>{content}</h1>);
      else if (level === 2) blocks.push(<h2 key={`b${key++}`} className={cls}>{content}</h2>);
      else blocks.push(<h3 key={`b${key++}`} className={cls}>{content}</h3>);
      i++;
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      flushPara();
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      blocks.push(
        <ul key={`b${key++}`} className="list-disc pl-5 mb-2">
          {items.map((it, idx) => <li key={idx}>{parseInline(it, `li${key}-${idx}`)}</li>)}
        </ul>
      );
      continue;
    }
    // 한 줄이 이미지들로만 이뤄지면 문단이 아니라 블록으로. 1개 = 단독 블록, 2개 이상 = **가로 갤러리 행**
    // (폭을 n등분 — 세로 사진 3~4장을 나란히). 테이블 문법 대신 이 규칙을 쓴다: 새 문법 없이
    // "같은 줄에 연달아 넣기"만으로 배열되고, 본문 폭(모바일 ~512px)에서도 n등분이라 깨지지 않는다.
    const imgOnly = /^\s*(?:!\[[^\]]*\]\([^)\s]+\)\s*)+$/.test(line);
    if (imgOnly) {
      flushPara();
      const imgs = Array.from(line.matchAll(/!\[([^\]]*)\]\(([^)\s]+)\)/g));
      if (imgs.length === 1) {
        blocks.push(<div key={`b${key++}`} className="mb-2">{parseInline(line.trim(), `img${key}`)}</div>);
      } else {
        blocks.push(
          <div key={`b${key++}`} className="flex gap-2 mb-2 items-start">
            {imgs.map((im, idx) => (
              <div key={idx} className="flex-1 min-w-0">
                {SAFE_IMAGE_URL.test(im[2])
                  ? <img src={im[2]} alt={im[1]} loading="lazy" className="w-full h-auto rounded" />
                  : <span className="text-xs text-gray-500">{im[1]}</span>}
              </div>
            ))}
          </div>
        );
      }
      i++;
      continue;
    }
    if (line.trim() === '') { flushPara(); i++; continue; }
    para.push(line);
    i++;
  }
  flushPara();
  return <>{blocks}</>;
}
